import React, { createContext, memo, useCallback, useEffect, useId, useMemo, useRef, useState } from "react"
import { SmoothScrollbar, useScrollbar, useScrollRig } from "@14islands/r3f-scroll-rig"
import styled from "styled-components"
import { ScrollTrigger } from "@repo/utils/gsap"
import { deepClone } from "@repo/utils"
import { DEFAULT_COLOR_SCHEME } from "../../styles/themeSettings"
import { useExtendedTheme } from "./Theme"
import useCustomContext from "../hooks/useCustomContext"
import { SCROLL_DIRECTION } from "@repo/utils/constants"
import { useRouter } from "next/router"
import { useDevice, useIsDesktop } from "../providers/DeviceProvider"
import { useWindowWidth } from "../hooks/useWindowSize"
import { useNavigation } from "../providers/NavigationProvider"

const Main = styled.main`
	position: relative;
	width: 100%;
	top: 0;
	left: 0;
`

const ScrollContext = createContext({
	instance: {},
	rig: {},
	// eslint-disable-next-line no-unused-vars
	refresh: (updatePosition = true) => {},
	main: null,
	hasUserScrolled: false,
	setHasUserScrolled: () => {},
	scrollToTop: (options) => {},
	registerColorSchemeComponent: () => {},
	unregisterColorSchemeComponent: () => {},
})

const CHANGE_COLOR_SCHEME_VELOCIY_BREAKPOINT = 100 //units of velocity

const Scroll = ({ children }) => {
	const MainRef = useRef(null)
	const isDesktop = useIsDesktop(true)
	const orientation = useDevice("orientation")
	const scrollRig = useScrollRig()
	const vWidth = useWindowWidth()
	const scrollbar = useScrollbar()
	const __lenis = scrollbar.__lenis
	const extendedTheme = useExtendedTheme()
	const [cs, setCs] = useState(DEFAULT_COLOR_SCHEME)
	const componentsRef = useRef(new Map())
	const isTransitioning = useNavigation((state) => state.isTransitioning)
	const isLoaded = useNavigation((state) => state.isLoaded)
	const { asPath, locale, isReady } = useRouter()
	const [hasUserScrolled, setHasUserScrolled] = useState(false)
	const hasUserScrolledRef = useRef(false)
	const setPosition = useRef(() => {})
	const debounceRefresh = useRef(0)
	const lastLoadedPathRef = useRef(null)
	const refresh = useRef(() => {})
	const scrollToTop = useRef(() => {})

	useEffect(() => {
		hasUserScrolledRef.current = hasUserScrolled
	}, [hasUserScrolled])

	scrollToTop.current = (options = {}) => {
		const { top = 0, ...rest } = options
		const t = top || 0
		if (!__lenis) {
			window.scrollTo({ top: t, behavior: "instant" })
		} else {
			__lenis.scrollTo(t, { immediate: true, force: true, ...rest })
		}
	}

	// function to calculate element position
	setPosition.current = (target) => {
		const currScroll = scrollbar.scroll.y || 0
		const positions = deepClone(target.el.getBoundingClientRect())
		//add scroll value to keep consistent breakpoints
		positions.top += currScroll
		positions.bottom += currScroll
		positions.y += currScroll
		target.positions = positions
	}

	const registerColorSchemeComponent = useCallback((key, data) => {
		if (!componentsRef.current.has(key)) {
			const clone = { ...data }
			setPosition.current(clone)
			if (observerRef.current) {
				observerRef.current.observe(clone.el, { box: "border-box" })
			}
			componentsRef.current.set(key, clone)
		}
	}, [])

	const unregisterColorSchemeComponent = useCallback((key) => {
		if (componentsRef.current.has(key)) {
			componentsRef.current.delete(key)
		}
	}, [])

	refresh.current = (updatePosition = true, safe = true) => {
		clearTimeout(debounceRefresh.current)
		debounceRefresh.current = setTimeout(() => {
			if (updatePosition) componentsRef.current.forEach((component) => setPosition.current(component))
			scrollRig.reflow()
			ScrollTrigger.refresh(safe)
		}, 50)
	}

	useEffect(() => {
		if (!__lenis) return
		isTransitioning ? __lenis?.stop() : __lenis?.start()
		refresh.current()
	}, [__lenis, isTransitioning])

	useEffect(() => {
		if (!isLoaded) return
		refresh.current()
		const isFirstLoad = !lastLoadedPathRef.current
		const isRouteChange = lastLoadedPathRef.current && lastLoadedPathRef.current !== asPath
		if (isFirstLoad || isRouteChange) {
			scrollToTop.current()
		}
		lastLoadedPathRef.current = asPath
	}, [isLoaded, asPath])

	const observerRef = useRef(null)

	useEffect(() => {
		setHasUserScrolled(false)
		hasUserScrolledRef.current = false
	}, [isDesktop, locale, isLoaded, isReady])

	useEffect(() => {
		refresh.current()
	}, [vWidth, extendedTheme.mode])

	useEffect(() => {
		refresh.current()
	}, [orientation])

	useEffect(() => {
		// toggle color scheme on scroll
		// inherit a scheme from the element in the middle of the screen (vertically)
		// ignore rapid scrolling
		function handleCsOnScroll(lenis) {
			const { scroll, dimensions, velocity } = lenis
			const halfScreen = scroll + dimensions.height / 2
			if (!(Math.abs(velocity) < CHANGE_COLOR_SCHEME_VELOCIY_BREAKPOINT)) return
			let csElementInMiddle = undefined

			const values = componentsRef.current.values()
			for (const element of values) {
				const { top, bottom } = element.positions
				if (top < halfScreen && halfScreen < bottom) {
					csElementInMiddle = element
					break
				}
			}
			const cs = csElementInMiddle?.scheme || DEFAULT_COLOR_SCHEME
			setCs(cs)
		}

		return scrollbar?.onScroll(handleCsOnScroll)
	}, [scrollbar])

	useEffect(() => {
		const markFirst = () => {
			if (!hasUserScrolledRef.current) {
				setHasUserScrolled(true)
				hasUserScrolledRef.current = true
			}
		}
		window.addEventListener("scroll", markFirst, { passive: true })
		window.addEventListener("touchmove", markFirst, { passive: true })
		window.addEventListener("wheel", markFirst, { passive: true })

		return () => {
			window.removeEventListener("scroll", markFirst)
			window.removeEventListener("touchmove", markFirst)
			window.removeEventListener("wheel", markFirst)
		}
	}, [])

	useEffect(() => {
		//debounce setCs to avoid flickering

		//NDW
		// console.log(cs, performance.now())
		const timeout = setTimeout(() => {
			extendedTheme.setTheme({ colorScheme: cs })
		}, 100)

		return () => {
			clearTimeout(timeout)
		}
	}, [cs])

	const context = useMemo(
		() => ({
			instance: scrollbar,
			rig: scrollRig,
			refresh: () => refresh.current(),
			main: MainRef.current,
			hasUserScrolled,
			scrollToTop: () => scrollToTop.current(),
			registerColorSchemeComponent,
			unregisterColorSchemeComponent,
		}),
		[scrollbar, scrollRig, hasUserScrolled, registerColorSchemeComponent, unregisterColorSchemeComponent],
	)

	return (
		<ScrollContext.Provider value={context}>
			{isLoaded && <SmoothScrollbar scrollRestoration="manual" key="smooth-scrollbar-key" enabled={isDesktop} />}

			<Main id="main" ref={MainRef} key="main">
				{children}
			</Main>
		</ScrollContext.Provider>
	)
}

export function useScrollContext(selector) {
	return useCustomContext(ScrollContext, selector)
}

export function useScrollDirection() {
	const [direction, setDirection] = useState(SCROLL_DIRECTION.DOWN)
	const scrollbar = useScrollbar()

	useEffect(() => {
		return scrollbar?.onScroll(({ direction }) => {
			setDirection(direction === -1 ? SCROLL_DIRECTION.UP : SCROLL_DIRECTION.DOWN)
		})
	}, [scrollbar])

	return direction
}

export function useColorSchemeComponent({ scheme = DEFAULT_COLOR_SCHEME } = {}) {
	const orientation = useDevice("orientation")
	const ref = useRef(null)
	const id = useId()
	const { registerColorSchemeComponent, unregisterColorSchemeComponent } = useScrollContext((state) => ({
		registerColorSchemeComponent: state.registerColorSchemeComponent,
		unregisterColorSchemeComponent: state.unregisterColorSchemeComponent,
	}))

	useEffect(() => {
		function cleanup() {
			return unregisterColorSchemeComponent(id)
		}

		if (!ref.current || !scheme) return cleanup()
		registerColorSchemeComponent(id, {
			el: ref.current,
			scheme,
		})
		return () => cleanup()
	}, [id, registerColorSchemeComponent, unregisterColorSchemeComponent, scheme, orientation])
	return { ref }
}

export default memo(Scroll)
